import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  breadcrumbs?: BreadcrumbItem[];
  noindex?: boolean;
  keywords?: string;
}

const SITE_URL = "https://premierpharma.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-social-card.jpg`;

function buildBreadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export default function SEO({
  title,
  description,
  path,
  image,
  type = "website",
  jsonLd,
  breadcrumbs,
  noindex,
  keywords,
}: SEOProps) {
  const baseTitle = "Premier Pharma";
  const fullTitle = title
    ? (title.includes(baseTitle) ? title : `${title} | ${baseTitle}`)
    : `${baseTitle} | Wholesale Generic Pharmaceuticals`;
  const desc =
    description ||
    "Premier Pharma — wholesale generic pharmaceuticals for licensed healthcare providers: pharmacies, clinics, doctor's offices, and veterinary practices. Competitive pricing, DSCSA-compliant, dedicated support.";
  const location = useLocation();
  const canonicalPath = path ?? location.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ogImageType = ogImage.match(/\.png(\?|$)/i)
    ? "image/png"
    : ogImage.match(/\.gif(\?|$)/i)
    ? "image/gif"
    : "image/jpeg";

  const jsonLdEntries: Record<string, any>[] = [];
  if (jsonLd) {
    if (Array.isArray(jsonLd)) jsonLdEntries.push(...jsonLd);
    else jsonLdEntries.push(jsonLd);
  }
  if (breadcrumbs && breadcrumbs.length > 0) {
    jsonLdEntries.push(buildBreadcrumbJsonLd(breadcrumbs));
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:site_name" content={baseTitle} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content={ogImageType} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@PremierPharmaID" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      {jsonLdEntries.map((entry, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
